import { CourseDto, CourseDtoTypeEnum, PaymentDto, PaymentDtoTypeEnum } from 'api/generated';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions, selectors } from 'store';

/**
 * Which upgrade surface to show. Null/undefined means the existing behaviour, so the switch is
 * opt-in per course. Always compare against an explicit value — the column is nullable with no
 * default, so every API payload carries `upsellMode: null` and `null !== undefined` is true.
 * That exact mistake is why `bundleUpsellId` never took effect.
 */
export const UpsellMode = {
  Legacy: 'legacy',
  Builder: 'builder',
  None: 'none',
} as const;

export type UpsellModeValue = typeof UpsellMode[keyof typeof UpsellMode];

/**
 * The builder columns are not in the generated `CourseDto` — the OpenAPI spec the client is built
 * from predates them — so they are declared here, as `bundleUpsellId` and `upsellOptionIds`
 * already were. `upsellMode` is optional AND nullable on purpose: the column is nullable with no
 * default, so a payload really does carry `upsellMode: null`, which is why the reads below compare
 * against explicit values rather than testing for undefined.
 */
type CourseWithBundleUpsell = CourseDto & {
  bundleUpsellId?: string;
  upsellOptionIds?: string[];
  upsellMode?: UpsellModeValue;
};

export const useBundleUpsell = () => {
  const dispatch = useDispatch();
  const courses = useSelector(selectors.courses.selectCourses);
  const accessToken = useSelector(selectors.auth.selectAccessToken);

  const getAccessToken = () => {
    return (
      accessToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      ''
    );
  };

  const handleCourseUpgrade = useCallback(
    async (course: CourseWithBundleUpsell): Promise<boolean> => {
      const courseFromStore = courses?.find((c) => c.id === course.id);
      const sourceCourse = courseFromStore || course;

      const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:4000';
      const token = getAccessToken();

      // Ensure we have the bundleUpsellId if it was passed in the original course object
      // but is missing from the store version (e.g. due to different DTO shapes)
      const explicitUpsellBundleId =
        (sourceCourse as CourseWithBundleUpsell).bundleUpsellId || (course as CourseWithBundleUpsell).bundleUpsellId;

      let resolvedSourceCourse: CourseWithBundleUpsell = sourceCourse;

      // Only fetch when the store cannot answer the routing question. `upsellMode === undefined`
      // means the field never arrived (a payload from before the backend exposed it); `null` is a
      // real "legacy" answer per the contract at the top of this file, so it must NOT refetch.
      // /courses/activatedByCustomer now carries upsellMode/upsellOptionIds/bundleUpsellId, which
      // makes this pre-modal round trip the exception instead of a blocking step on every click.
      if ((sourceCourse as CourseWithBundleUpsell).upsellMode === undefined) {
        try {
          const response = await fetch(`${baseUrl}/courses/${sourceCourse.id}/public`, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const publicCourse = await response.json();
            resolvedSourceCourse = {
              ...(sourceCourse as CourseWithBundleUpsell),
              ...publicCourse,
            };
          }
        } catch (_e) {
          // ignore fallback fetch errors
        }
      }

      // The per-course upsell switch. This is the single choke point every Upgrade CTA in the app
      // funnels through, so branching here covers all of them with no page edits.
      //
      // It sits after the /public merge above (which is where upsellMode arrives) and before the
      // study-material early return below, so a study course can never be routed into the Bundle
      // Builder even if its mode was set by mistake.
      const upsellMode = (resolvedSourceCourse as CourseWithBundleUpsell).upsellMode;

      if (upsellMode === UpsellMode.None) {
        return false;
      }

      if (upsellMode === UpsellMode.Builder) {
        const isStudyMaterial =
          sourceCourse.type === CourseDtoTypeEnum.Default || (sourceCourse.type as string) === 'default';

        if (!isStudyMaterial) {
          // The chooser, not the builder directly: which flow the learner should see (upgrade this
          // course / build a bundle / buy or change the bundle they're trialling) is a fact about
          // the LEARNER, resolved server-side when the chooser opens. Opening the builder here
          // would hard-wire every learner into one flow and make the trial flow unreachable.
          dispatch(actions.courses.openUpgradeChooserModal({ sourceCourseId: sourceCourse.id }));
          // Must return true: TestMode does not fall back to pricing on false, it opens the
          // manual/bonus upgrade modal — so returning false here would double-open.
          return true;
        }
      }

      const explicitUpsellOptionIds =
        (resolvedSourceCourse as CourseWithBundleUpsell).upsellOptionIds ||
        (course as CourseWithBundleUpsell).upsellOptionIds;

      console.log('🔍 [useBundleUpsell] handleCourseUpgrade called', {
        courseId: course.id,
        courseName: course.name,
        isBundle: (sourceCourse.type as string) === 'bundle',
        explicitUpsellBundleId,
        explicitUpsellOptionIds,
        sourceCourseBundleUpsellId: (sourceCourse as CourseWithBundleUpsell).bundleUpsellId,
        inputCourseBundleUpsellId: (course as CourseWithBundleUpsell).bundleUpsellId,
        resolvedUpsellOptionIds: (resolvedSourceCourse as CourseWithBundleUpsell).upsellOptionIds,
        resolvedSourceCourseId: resolvedSourceCourse.id,
      });

      const defaultPayment: PaymentDto = {
        type: PaymentDtoTypeEnum.Free,
        onPause: false,
      };

      const normalizeUpsellOptionIds = (ids: unknown): string[] => {
        if (!Array.isArray(ids)) return [];
        return ids.map((id) => (typeof id === 'string' ? id.trim() : '')).filter((id) => Boolean(id));
      };

      const resolveUpsellOptions = async (ids: string[]): Promise<CourseDto[]> => {
        // Concurrently, order preserved. These fetches are independent, but they used to be
        // awaited one at a time — with a ~300ms staging round trip, N options froze the click
        // for N x 300ms. The bundles-fetch-fresh contract below is about FRESHNESS (the Redux
        // copy of a bundle's relations goes stale), not ordering, so it survives unchanged.
        // `cache: 'no-store'` already defeats the browser cache; the old `?ts=` param only
        // shredded any shared-cache keying on top of that, so it is dropped.
        const settled = await Promise.all(
          ids.map(async (id): Promise<CourseDto | null> => {
            const fromStore = courses?.find((c) => c.id === id);
            const isBundleFromStore = (fromStore?.type as string) === 'bundle';

            // For non-bundle courses, store data is enough.
            // For bundles, always fetch fresh public payload to avoid stale cached relation data.
            if (fromStore && !isBundleFromStore) {
              return fromStore;
            }

            try {
              const response = await fetch(`${baseUrl}/courses/${id}/public`, {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  'Content-Type': 'application/json',
                  'Cache-Control': 'no-cache',
                  Pragma: 'no-cache',
                },
                cache: 'no-store',
              });

              if (!response.ok) {
                return fromStore ?? null;
              }

              const publicCourse = await response.json();
              return {
                ...publicCourse,
                isActive: true,
                currentPayment: defaultPayment,
              };
            } catch (_e) {
              // ignore individual option fetch failures
              return fromStore ?? null;
            }
          }),
        );

        return settled.filter((course): course is CourseDto => Boolean(course));
      };

      const resolveBundleUpsellIds = async (bundleCourse: CourseWithBundleUpsell): Promise<string[]> => {
        const fallbackIds = normalizeUpsellOptionIds(bundleCourse.upsellOptionIds);
        if (!baseUrl || !bundleCourse?.id) return fallbackIds;

        try {
          const response = await fetch(`${baseUrl}/courses/${bundleCourse.id}/public`, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) return fallbackIds;
          const publicBundle = await response.json();
          return normalizeUpsellOptionIds(publicBundle?.upsellOptionIds);
        } catch (_e) {
          return fallbackIds;
        }
      };

      const splitBundleOption = (options: CourseDto[], fallbackBundle?: CourseDto) => {
        const bundleOption = options.find((option) => (option.type as string) === 'bundle');
        if (bundleOption) {
          return {
            bundle: bundleOption,
            bundleCourses: options.filter((option) => option.id !== bundleOption.id),
          };
        }

        if (fallbackBundle && (fallbackBundle.type as string) === 'bundle') {
          return {
            bundle: fallbackBundle,
            bundleCourses: options.filter((option) => option.id !== fallbackBundle.id),
          };
        }

        return {
          bundle: options[0],
          bundleCourses: options.slice(1),
        };
      };

      const openPricingFor = (target: CourseDto) => {
        dispatch(actions.courses.selectCourse(target));
        dispatch(actions.courses.openChooseProductModalOpen());
      };

      const hasUpsellOptionIdsField =
        (resolvedSourceCourse as CourseWithBundleUpsell).upsellOptionIds !== undefined ||
        (course as CourseWithBundleUpsell).upsellOptionIds !== undefined;

      const normalizedExplicitUpsellOptionIds = normalizeUpsellOptionIds(explicitUpsellOptionIds);
      const paymentStructureRaw = (sourceCourse as CourseWithBundleUpsell & { paymentStructure?: unknown })
        .paymentStructure;
      const paymentStructure =
        typeof paymentStructureRaw === 'string'
          ? paymentStructureRaw.trim().toLowerCase()
          : typeof paymentStructureRaw === 'object' && paymentStructureRaw !== null
          ? (
              (paymentStructureRaw as { id?: string; value?: string }).id ||
              (paymentStructureRaw as { id?: string; value?: string }).value ||
              ''
            )
              .trim()
              .toLowerCase()
          : '';
      const hasStudySubscriptions =
        Boolean(sourceCourse.prices?.monthlySubscriptions) || Boolean(sourceCourse.prices?.annualSubscriptions);
      const hasInstallment = Boolean(sourceCourse.prices?.installment);
      const hasPurchase = Boolean(sourceCourse.prices?.purchase);
      const isStudyMaterialCourse =
        sourceCourse.type === CourseDtoTypeEnum.Default ||
        (sourceCourse.type as string) === 'default' ||
        paymentStructure === 'study' ||
        hasStudySubscriptions ||
        (!hasInstallment && hasPurchase);

      if (isStudyMaterialCourse && normalizedExplicitUpsellOptionIds.length === 0) {
        openPricingFor(sourceCourse);
        return true;
      }

      // Prefer explicit admin-configured upsell options for both courses and bundles.

      // If the field exists but is empty, treat it as "no upsell configured".
      // This ensures empty Upsell Options opens pricing directly (caller fallback).
      if (hasUpsellOptionIdsField && normalizedExplicitUpsellOptionIds.length === 0) {
        return false;
      }

      if (normalizedExplicitUpsellOptionIds.length > 0) {
        const upsellOptions = await resolveUpsellOptions(normalizedExplicitUpsellOptionIds);

        console.log('🔍 [useBundleUpsell] resolved upsell options', {
          requestedIds: normalizedExplicitUpsellOptionIds,
          resolvedIds: upsellOptions.map((option) => option.id),
          resolvedNames: upsellOptions.map((option) => option.name),
          resolvedTypes: upsellOptions.map((option) => option.type),
        });

        if (upsellOptions.length > 0) {
          const { bundle, bundleCourses } = splitBundleOption(
            upsellOptions,
            (sourceCourse.type as string) === 'bundle' ? sourceCourse : undefined,
          );

          dispatch(
            actions.courses.openBundleUpgradeOptionsModal({
              bundle,
              bundleCourses,
              isUpsellModal: true,
              upsellOrderIds: normalizedExplicitUpsellOptionIds,
            }),
          );

          return true;
        }
      }

      // If the user is on a bundle trial, upsell options should come from the bundle configuration
      // (not from the individual course inside the bundle).
      const isTrialAccessForCurrentCourse = sourceCourse.currentPayment?.type === PaymentDtoTypeEnum.Free;
      const trialBundles = (courses || []).filter(
        (c) => (c.type as string) === 'bundle' && c.currentPayment?.type === PaymentDtoTypeEnum.Free,
      );
      const trialBundle = trialBundles[0] as CourseWithBundleUpsell | undefined;

      if ((sourceCourse.type as string) !== 'bundle' && isTrialAccessForCurrentCourse && trialBundle) {
        const bundleHasUpsellOptionIdsField = trialBundle.upsellOptionIds !== undefined;
        const bundleUpsellIds = await resolveBundleUpsellIds(trialBundle);

        // Bundle trial takes precedence only when no explicit upsell options are configured.
        if (bundleHasUpsellOptionIdsField && bundleUpsellIds.length === 0) {
          openPricingFor(trialBundle);
          return true;
        }
        if (bundleUpsellIds.length > 0) {
          const upsellOptions = await resolveUpsellOptions(bundleUpsellIds);

          if (upsellOptions.length > 0) {
            const { bundle, bundleCourses } = splitBundleOption(upsellOptions, trialBundle);

            dispatch(
              actions.courses.openBundleUpgradeOptionsModal({
                bundle,
                bundleCourses,
                isUpsellModal: true,
                upsellOrderIds: bundleUpsellIds,
              }),
            );

            return true;
          }
        }
      }

      // If the current course is a bundle, open the bundle upgrade options modal directly.
      // This is the desired flow for "orange Upgrade" clicks on bundle-based sections.
      // NOTE: For new behavior, bundles only show an upsell modal when upsellOptionIds are configured.
      // We keep this legacy fallback only when the upsellOptionIds field is not present.
      if ((sourceCourse.type as string) === 'bundle' && !hasUpsellOptionIdsField) {
        const nonBundleCourses = courses?.filter((c) => (c.type as string) !== 'bundle') || [];

        if (nonBundleCourses.length > 0) {
          dispatch(
            actions.courses.openBundleUpgradeOptionsModal({
              bundle: sourceCourse,
              bundleCourses: nonBundleCourses,
            }),
          );
          return true;
        }
        // If we have no non-bundle courses to display, let caller fall back.
        return false;
      }

      // For non-bundle courses, attempt to discover an upsell bundle via API.
      // This is intentionally resilient to partial CourseDto objects that may not include `bundleUpsellId`.
      try {
        // If Bundle Upsell is explicitly set on the course, prefer it.
        // This avoids falling through to pricing when the API call fails for transient reasons.
        if (explicitUpsellBundleId) {
          console.log('🔍 [useBundleUpsell] Found explicit upsell ID:', explicitUpsellBundleId);
          const upsellBundleFromStore = courses?.find((c) => c.id === explicitUpsellBundleId);

          const nonBundleCourses = courses?.filter((c) => (c.type as string) !== 'bundle') || [];
          const upsellModalCourses = nonBundleCourses.length > 0 ? nonBundleCourses : [sourceCourse];

          if (upsellBundleFromStore) {
            console.log('🔍 [useBundleUpsell] Found upsell bundle in store, opening modal');
            dispatch(
              actions.courses.openBundleUpgradeOptionsModal({
                bundle: upsellBundleFromStore,
                bundleCourses: upsellModalCourses,
                isUpsellModal: true,
                upsellOrderIds: normalizedExplicitUpsellOptionIds,
              }),
            );
            return true;
          }
          // If we can't find it in-store, fetch the upsell bundle directly.
          if (baseUrl) {
            try {
              console.log(
                '🔍 [useBundleUpsell] Fetching public bundle:',
                `${baseUrl}/courses/${explicitUpsellBundleId}/public`,
              );
              const publicResponse = await fetch(`${baseUrl}/courses/${explicitUpsellBundleId}/public`, {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  'Content-Type': 'application/json',
                },
              });

              if (publicResponse.ok) {
                const publicBundle = await publicResponse.json();
                console.log('🔍 [useBundleUpsell] Fetched public bundle successfully:', publicBundle.name);

                // Convert PublicCourseDto -> CourseDto by adding required fields (minimal defaults)
                const bundleAsCourseDto: CourseDto = {
                  ...publicBundle,
                  isActive: true,
                  currentPayment: defaultPayment,
                };

                dispatch(
                  actions.courses.openBundleUpgradeOptionsModal({
                    bundle: bundleAsCourseDto,
                    bundleCourses: upsellModalCourses,
                    isUpsellModal: true,
                    upsellOrderIds: normalizedExplicitUpsellOptionIds,
                  }),
                );

                return true;
              } else {
                console.warn('🔍 [useBundleUpsell] Failed to fetch public bundle:', publicResponse.status);
              }
            } catch (e) {
              console.error('🔍 [useBundleUpsell] Error fetching explicit upsell bundle', e);
            }
          }
          // If we can't fetch it directly, fall back to course-based upsell endpoint.
        }

        if (!baseUrl) {
          console.warn('🔍 [useBundleUpsell] No base URL found');
          return false;
        }

        console.log('🔍 [useBundleUpsell] Checking /bundle-upsell endpoint for course:', sourceCourse.id);
        const response = await fetch(`${baseUrl}/courses/${sourceCourse.id}/bundle-upsell`, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          console.warn('🔍 [useBundleUpsell] /bundle-upsell endpoint returned:', response.status);
          // 404/204/etc => no upsell available.
          // Fall through to trial-bundle upgrade options.
          throw new Error('No upsell bundle');
        }

        const bundleUpsellCourse = await response.json();
        console.log('🔍 [useBundleUpsell] /bundle-upsell response:', bundleUpsellCourse);
        const upsellBundleId = bundleUpsellCourse?.id || bundleUpsellCourse?.bundleUpsellId;
        if (!bundleUpsellCourse || !upsellBundleId) throw new Error('Invalid upsell response');

        // Prefer the fully-populated bundle from the store, otherwise use API data.
        const upsellBundleFromStore = courses?.find((c) => c.id === upsellBundleId);

        // Convert PublicCourseDto -> CourseDto by adding required fields (minimal defaults)
        const bundleAsCourseDto: CourseDto = upsellBundleFromStore
          ? upsellBundleFromStore
          : {
              ...bundleUpsellCourse,
              isActive: true,
              currentPayment: defaultPayment,
            };

        const nonBundleCourses = courses?.filter((c) => (c.type as string) !== 'bundle') || [];
        const upsellModalCourses = nonBundleCourses.length > 0 ? nonBundleCourses : [sourceCourse];

        dispatch(
          actions.courses.openBundleUpgradeOptionsModal({
            bundle: bundleAsCourseDto,
            bundleCourses: upsellModalCourses,
            isUpsellModal: true,
          }),
        );

        return true;
      } catch (_error) {
        console.warn('🔍 [useBundleUpsell] Error in handleCourseUpgrade:', _error);
        // Home parity: if user has a trial bundle, open bundle upgrade options modal.
        const userBundles = courses?.filter((c) => (c.type as string) === 'bundle') || [];
        const trialBundles = userBundles.filter((bundle) => {
          if (!bundle.currentPayment) return true;
          return bundle.currentPayment.type === PaymentDtoTypeEnum.Free;
        });

        const relevantBundle = trialBundles[0] as CourseWithBundleUpsell | undefined;

        if (relevantBundle) {
          const relevantBundleHasUpsellOptionIdsField = relevantBundle.upsellOptionIds !== undefined;
          const relevantBundleUpsellIds = await resolveBundleUpsellIds(relevantBundle);

          if (relevantBundleHasUpsellOptionIdsField && relevantBundleUpsellIds.length === 0) {
            openPricingFor(relevantBundle);
            return true;
          }

          if (relevantBundleUpsellIds.length > 0) {
            const upsellOptions = await resolveUpsellOptions(relevantBundleUpsellIds);

            if (upsellOptions.length > 0) {
              const { bundle, bundleCourses } = splitBundleOption(upsellOptions, relevantBundle);

              dispatch(
                actions.courses.openBundleUpgradeOptionsModal({
                  bundle,
                  bundleCourses,
                  isUpsellModal: true,
                  upsellOrderIds: relevantBundleUpsellIds,
                }),
              );

              return true;
            }
          }
        }

        return false;
      }

      // No bundle upsell available or error occurred
      return false;
    },
    [dispatch, courses],
  );

  const openRegularUpgrade = useCallback(
    (course: CourseDto) => {
      dispatch(actions.courses.selectCourse(course));
      dispatch(actions.courses.openChooseProductModalOpen());
    },
    [dispatch],
  );

  const handleUpgradeBundle = useCallback(
    async (course?: CourseDto | null) => {
      if (!course) {
        dispatch(actions.courses.openChooseProductModalOpen());
        return;
      }

      const handled = await handleCourseUpgrade(course);
      if (handled) return;

      openRegularUpgrade(course);
    },
    [dispatch, handleCourseUpgrade, openRegularUpgrade],
  );

  const handleUpgradeWithBundleUpsellCheck = useCallback(
    async (course: CourseDto, callback?: () => void) => {
      try {
        const handledByBundleUpsell = await handleCourseUpgrade(course);

        if (callback) callback();
        return handledByBundleUpsell;
      } catch (error) {
        return false;
      }
    },
    [handleCourseUpgrade],
  );

  return {
    handleCourseUpgrade,
    openRegularUpgrade,
    handleUpgradeWithBundleUpsellCheck,
    handleUpgradeBundle,
  };
};
